/**
 * Per-viewer, per-tenant snooze store, keyed by row resource_name.
 *
 * G-5 says the snooze duration is 30 days. This is enough to hide a
 * recommendation from the operator's daily list without pretending Core has
 * saved the snooze anywhere Google can see. Backend recommendations do not
 * carry a first-class snooze concept in the Google Ads API, so this ships as a
 * per-viewer localStorage record until a shared snooze table exists.
 *
 * Keys are namespaced under `ads.snooze.<tenant>.<resource>` so two tenants
 * viewed by the same operator do not share snooze state.
 */
const NAMESPACE = 'ads.snooze';
const SNOOZE_DAYS = 30;

function storageKey(tenantId: string, resourceName: string): string {
  return `${NAMESPACE}.${tenantId}.${resourceName}`;
}

function tryGetStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function isSnoozed(
  tenantId: string | null | undefined,
  resourceName: string,
  now: number = Date.now(),
): boolean {
  if (!tenantId) return false;
  const store = tryGetStorage();
  if (!store) return false;
  try {
    const raw = store.getItem(storageKey(tenantId, resourceName));
    if (!raw) return false;
    const expiresAt = Number.parseInt(raw, 10);
    if (!Number.isFinite(expiresAt)) return false;
    if (expiresAt <= now) {
      store.removeItem(storageKey(tenantId, resourceName));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function snooze(
  tenantId: string | null | undefined,
  resourceName: string,
  now: number = Date.now(),
): void {
  if (!tenantId) return;
  const store = tryGetStorage();
  if (!store) return;
  const expiresAt = now + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  try {
    store.setItem(storageKey(tenantId, resourceName), String(expiresAt));
  } catch {
    // Private-mode / quota exhaustion is silent: the row simply stays visible.
  }
}

export function unsnooze(
  tenantId: string | null | undefined,
  resourceName: string,
): void {
  if (!tenantId) return;
  const store = tryGetStorage();
  if (!store) return;
  try {
    store.removeItem(storageKey(tenantId, resourceName));
  } catch {
    // ignored
  }
}

export const SNOOZE_DURATION_DAYS = SNOOZE_DAYS;
